import { PropsWithChildren, ReactNode, useState, MouseEvent, ComponentPropsWithRef } from 'react';
import { Box, BoxProps, Flex } from 'reflexbox';
import styled, { css } from 'styled-components';
import { darken, mix, rgba } from 'polished';
import { useFormikContext } from 'formik';

import { Spinner } from './Spinner';
import { useUpdateEffect } from 'react-use';

import { CheckmarkIcon } from '@treams/treams-icons';
import { Link } from '@treams/treams-routes';
import { Skeleton } from './Skeleton';

const Icon = styled(Box)`
	flex-grow: 0;
	flex-shrink: 0;

	svg {
		display: block;
		width: 24px;
		height: 24px;
		margin: -2px;
	}
`;

const Primary = css`
	background: ${(props) => props.theme.colors.primary};
	border: 1px solid ${(props) => props.theme.colors.primary};
	color: white;
	border-radius: 5px;

	&:hover {
		box-shadow:
			0px 2px 4px ${(props) => rgba(props.theme.colors.primary, 0.16)},
			0px 8px 16px ${(props) => rgba(props.theme.colors.primary, 0.25)};
		transform: translateY(-2px);

		&:active {
			box-shadow: none;
			background: ${(props) => props.theme.colors.primaryDark};
			border: 1px solid ${(props) => props.theme.colors.primaryDark};
			transform: translateY(2px);
		}
	}

	${Icon} {
		svg path {
			fill: white;
		}
	}
`;

const SecondaryNormal = css`
	background: white;
	border: 1px solid ${(props) => mix(0.75, props.theme.colors.white, props.theme.colors.slateBlue)};
	color: ${(props) => props.theme.colors.slateBlue};
	box-sizing: border-box;
	border-radius: 5px;
`;

const Secondary = css`
	${SecondaryNormal}

	&:hover {
		border-color: ${(props) => mix(0.9, props.theme.colors.white, props.theme.colors.slateBlue)};
		background: ${(props) => mix(0.9, props.theme.colors.white, props.theme.colors.slateBlue)};

		&:active {
			border-color: ${(props) => mix(0.8, props.theme.colors.white, props.theme.colors.slateBlue)};
			background: ${(props) => mix(0.8, props.theme.colors.white, props.theme.colors.slateBlue)};
		}
	}

	${Icon} {
		svg path {
			fill: ${(props) => props.theme.colors.slateBlue};
		}
	}
`;

const Destructive = css<{ $loading?: boolean; success?: boolean }>`
	border: 1px solid ${(props) => mix(0.75, props.theme.colors.white, props.theme.colors.danger)};
	color: ${(props) => props.theme.colors.danger};
	box-sizing: border-box;
	border-radius: 5px;

	&:hover {
		border-color: ${(props) => mix(0.9, props.theme.colors.white, props.theme.colors.danger)};
		background: ${(props) => mix(0.9, props.theme.colors.white, props.theme.colors.danger)};

		&:active {
			border-color: ${(props) => mix(0.8, props.theme.colors.white, props.theme.colors.danger)};
			background: ${(props) => mix(0.8, props.theme.colors.white, props.theme.colors.danger)};
		}
	}

	${Icon} {
		svg path {
			${(props) =>
				!props.$loading &&
				!props.success &&
				`
				fill: ${props.theme.colors.danger};
			`}

			${(props) =>
				props.$loading &&
				`
				fill: ${props.theme.colors.slateBlue};
			`}

			${(props) =>
				props.success &&
				`
				fill: ${props.theme.colors.white};
			`}
		}
	}
`;

const Plain = css`
	border: 1px solid transparent;
	font-weight: 400;
	box-sizing: border-box;
	border-radius: 5px;
	text-decoration: underline;

	&:hover {
		border-color: ${(props) => mix(0.9, props.theme.colors.white, props.theme.colors.slateBlue)};
		background: ${(props) => mix(0.9, props.theme.colors.white, props.theme.colors.slateBlue)};

		&:active {
			border-color: ${(props) => mix(0.8, props.theme.colors.white, props.theme.colors.slateBlue)};
			background: ${(props) => mix(0.8, props.theme.colors.white, props.theme.colors.slateBlue)};
		}
	}

	${Icon} {
		svg path {
			fill: ${(props) => props.theme.colors.slateBlue};
		}
	}
`;

const Transparent = css`
	background: ${(props) => rgba(props.theme.colors.primary, 0.15)};
	border: 1px solid transparent;
	color: ${(props) => props.theme.colors.primary};
	font-weight: bold;
	box-sizing: border-box;
	border-radius: 5px;

	svg path {
		fill: ${(props) => props.theme.colors.primary};
	}

	&:hover {
		background: ${(props) => rgba(props.theme.colors.primary, 0.25)};

		&:active {
			background: ${(props) => rgba(props.theme.colors.primary, 0.5)};
			color: ${(props) => darken(0.05, props.theme.colors.primary)};

			svg path {
				fill: ${(props) => darken(0.05, props.theme.colors.primary)};
			}
		}
	}
`;

const WhiteTransparent = css`
	${({ theme }) => css`
		border-radius: 5px;
		font-weight: bold;
		border: 1px solid transparent;
		color: ${theme.colors.white};
		background: ${theme.colors.WhiteTransparantMediumSoft};

		&:hover {
			background-color: ${theme.colors.WhiteTransparantMedium};
		}

		&:active {
			background-color: ${theme.colors.WhiteTransparantMediumHard};
		}
	`}
`;

const Card = css`
	color: ${(props) => props.theme.colors.slateBlue};
	border-radius: 5px;
	background: ${(props) => props.theme.colors.white};
	border: 1px solid #c2cad2;
	box-shadow: 0px 2px 4px rgba(194, 202, 210, 0.25);

	&:hover {
		background: ${(props) => props.theme.colors.whiteBlueLight};
		border-color: ${(props) => rgba(props.theme.colors.slateBlue, 0.5)};

		&:active {
			background: ${(props) => props.theme.colors.lightBlue};
		}
	}

	&:focus,
	&:hover:focus,
	&:hover:active:focus {
		border: 1px solid ${(props) => props.theme.colors.primary};
		box-shadow: 0px 0px 0px 2px
			${(props) => mix(0.5, props.theme.colors.primary, props.theme.colors.white)};
	}
`;

const Disabled = css`
	border: 1px solid ${(props) => props.theme.colors.lightBlueLight};
	color: ${(props) => props.theme.colors.lightBlueDark};
	svg path {
		fill: ${(props) => props.theme.colors.lightBlueDark};
	}
	box-sizing: border-box;
	border-radius: 5px;
	cursor: default;
`;

type ButtonTypes =
	| 'primary'
	| 'secondary'
	| 'destructive'
	| 'plain'
	| 'transparent'
	| 'whiteTransparant'
	| 'card';

interface IButtonProps {
	type: ButtonTypes;
	$loading?: boolean;
	success?: boolean;
	disabled?: boolean;
	small?: boolean;
}

const ButtonStyle = css<IButtonProps>`
	position: relative;
	align-items: center;
	font-weight: bold;
	font-size: 15px;
	padding: 11px 14px;
	cursor: pointer;
	transition: 0.1s ease-out all;
	line-height: 22px;
	justify-content: center;

	${(props) => props.type === 'primary' && !props.disabled && Primary}
	${(props) => props.type === 'secondary' && !props.disabled && Secondary}
	${(props) => props.type === 'destructive' && !props.disabled && Destructive}
	${(props) => props.type === 'plain' && !props.disabled && Plain}
    ${(props) => props.type === 'card' && !props.disabled && Card}
	${(props) => props.type === 'transparent' && !props.disabled && Transparent}
	${(props) => props.type === 'whiteTransparant' && !props.disabled && WhiteTransparent}
	${(props) => props.disabled && Disabled}

	${(props) => props.small && `padding: 5px 14px;`}

	${(props) =>
		(props.$loading || props.success) &&
		css`
			&,
			&:hover,
			&:hover:active {
				box-shadow: none;
				padding-left: 34px;
				transform: none;
				cursor: default;
				border-color: ${props.$loading
					? props.theme.colors.lightBlue
					: props.theme.colors.greenDark};
				background: ${props.$loading ? props.theme.colors.lightBlue : props.theme.colors.greenDark};
				color: ${props.$loading ? props.theme.colors.slateBlue : props.theme.colors.white};
			}
		`}
`;

const Container = styled(Flex)<IButtonProps>`
	${ButtonStyle}
`;

const SpinnerContainer = styled(Box)<{ small?: boolean }>`
	position: absolute;
	top: ${(props) => (props.small ? '9' : '15')}px;
	left: 11px;

	svg {
		display: block;
		width: 24px;
		height: 24px;
		margin: -4px;

		path {
			fill: ${(props) => props.theme.colors.white};
		}
	}
`;

export function Button(
	props: PropsWithChildren<
		BoxProps & {
			loading?: boolean;
			icon?: ReactNode;
			rightIcon?: ReactNode;
			title?: ReactNode;
			link?: string;
			type?: ButtonTypes;
			small?: boolean;
			disabled?: boolean;
			disableSuccess?: boolean;
			target?: '_blank';
			onClick?: (event: MouseEvent) => Promise<void> | void;
			navigationState?: object;
		}
	>,
) {
	const { loading, title, disabled, type, onClick, target, ...rest } = props;

	const [success, setSuccess] = useState(false);
	const [waiting, setWaiting] = useState(false);

	useUpdateEffect(() => {
		if (props.loading || props.disableSuccess) {
			return;
		}

		setSuccess(true);

		setTimeout(() => setSuccess(false), 2000);
	}, [props.loading]);

	const isLoading = loading || waiting;

	if (props.link) {
		return (
			<Link
				to={props.link}
				target={target}
				rel={target ? 'noreferrer noopener' : undefined}
				navigationState={props.navigationState}
			>
				<Container
					$loading={isLoading}
					success={success}
					disabled={disabled}
					type={type ?? 'primary'}
					onClick={disabled ? undefined : onClick}
					tabIndex={0}
					{...rest}
				>
					{loading ? (
						<SpinnerContainer>
							<Spinner />
						</SpinnerContainer>
					) : (
						success && (
							<SpinnerContainer>
								<CheckmarkIcon />
							</SpinnerContainer>
						)
					)}
					{props.icon && <Icon mr={title ? 2 : 0}>{props.icon}</Icon>}
					{title}
					{props.rightIcon && <Icon ml={1}>{props.rightIcon}</Icon>}
				</Container>
			</Link>
		);
	}

	return (
		<Container
			$loading={isLoading}
			success={success}
			disabled={disabled}
			type={type ?? 'primary'}
			onClick={
				disabled
					? undefined
					: async (event: MouseEvent) => {
							setWaiting(true);
							await onClick?.(event);
							setWaiting(false);
					  }
			}
			tabIndex={0}
			{...rest}
		>
			{isLoading ? (
				<SpinnerContainer small={props.small}>
					<Spinner />
				</SpinnerContainer>
			) : (
				success && (
					<SpinnerContainer small={props.small}>
						<CheckmarkIcon />
					</SpinnerContainer>
				)
			)}
			{props.icon && <Icon mr={title ? 2 : 0}>{props.icon}</Icon>}
			{title}
			{props.rightIcon && <Icon ml={1}>{props.rightIcon}</Icon>}
		</Container>
	);
}

Button.Anchor = styled.a`
	display: block;
	${ButtonStyle}
`;

Button.Skeleton = styled(Skeleton.Box)<{ small?: boolean }>`
	${(props) =>
		props.small
			? `
			height: 35px;
			width: 110px;
		`
			: `
			height: 46px;
			width: 140px;
		`}
`;

Button.Styles = { Secondary, SecondaryNormal };

export function ButtonFormik(props: ComponentPropsWithRef<typeof Button>) {
	const formik = useFormikContext();

	return <Button {...props} loading={formik.isSubmitting} onClick={formik.submitForm} />;
}

Button.Formik = ButtonFormik;
